import { apiService } from './api.js';

const PROJECTS_BASE = '/projects';

// ============================================================================
// Project CRUD
// ============================================================================

export async function fetchProjects() {
  const response = await apiService.get(PROJECTS_BASE);
  return response.data.projects;
}

export async function createProject(name, description = null) {
  const response = await apiService.post(PROJECTS_BASE, { name, description });
  return response.data;
}

export async function getProject(projectId) {
  const response = await apiService.get(`${PROJECTS_BASE}/${projectId}`);
  return response.data;
}

export async function updateProject(projectId, updates) {
  const response = await apiService.put(`${PROJECTS_BASE}/${projectId}`, updates);
  return response.data;
}

export async function deleteProject(projectId) {
  await apiService.delete(`${PROJECTS_BASE}/${projectId}`);
}

// ============================================================================
// Project Members
// ============================================================================

export async function fetchProjectMembers(projectId) {
  const response = await apiService.get(`${PROJECTS_BASE}/${projectId}/members`);
  return response.data.members;
}

export async function addProjectMember(projectId, username) {
  const response = await apiService.post(`${PROJECTS_BASE}/${projectId}/members`, { username });
  return response.data;
}

export async function removeProjectMember(projectId, username) {
  await apiService.delete(`${PROJECTS_BASE}/${projectId}/members/${username}`);
}

export async function leaveProject(projectId) {
  await apiService.post(`${PROJECTS_BASE}/${projectId}/leave`);
}

export async function fetchOrgUsers() {
  const response = await apiService.get(`${PROJECTS_BASE}/org/users`);
  return response.data;
}

// ============================================================================
// Project Files
// ============================================================================

export async function fetchProjectFiles(projectId) {
  const response = await apiService.get(`${PROJECTS_BASE}/${projectId}/files`);
  return response.data.files;
}

export async function uploadProjectFile(projectId, file, onProgress = null) {
  const formData = new FormData();
  formData.append('dld8_file', file);

  const response = await apiService.post(`${PROJECTS_BASE}/${projectId}/files`, formData, {
    onUploadProgress: onProgress
      ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
      : undefined,
  });
  return response.data;
}

export async function deleteProjectFile(projectId, filename) {
  await apiService.delete(`${PROJECTS_BASE}/${projectId}/files/${encodeURIComponent(filename)}`);
}

export async function renameProjectFile(projectId, filename, newStem) {
  const response = await apiService.patch(
    `${PROJECTS_BASE}/${projectId}/files/${encodeURIComponent(filename)}`,
    { new_stem: newStem },
  );
  return response.data;
}

// ============================================================================
// File Info & Data
// ============================================================================

export async function getFileInfo(projectId, filename) {
  const response = await apiService.get(
    `${PROJECTS_BASE}/${projectId}/files/${encodeURIComponent(filename)}/info`,
  );
  return response.data;
}

export async function getFileBulkData(projectId, filename) {
  const response = await apiService.get(
    `${PROJECTS_BASE}/${projectId}/files/${encodeURIComponent(filename)}/bulk`,
  );
  return response.data;
}

export async function getFileHistory(projectId, filename) {
  const response = await apiService.get(
    `${PROJECTS_BASE}/${projectId}/files/${encodeURIComponent(filename)}/history`,
  );
  return response.data;
}

export async function getFileData(projectId, filename, chamber, options = {}) {
  const params = new URLSearchParams({
    chamber: String(chamber),
    start_time: String(options.startTime ?? 0),
    end_time: String(options.endTime ?? -1),
  });
  // Default data types: Time, O2, O2Flux
  const dataTypes = options.dataTypes || ['Time', 'O2', 'O2Flux'];
  dataTypes.forEach((dt) => params.append('data_types', dt));

  const response = await apiService.get(
    `${PROJECTS_BASE}/${projectId}/files/${encodeURIComponent(filename)}/data?${params}`,
  );
  return response.data;
}

export async function getFileMarks(projectId, filename, chamber) {
  const response = await apiService.get(
    `${PROJECTS_BASE}/${projectId}/files/${encodeURIComponent(filename)}/marks`,
    { params: { chamber: String(chamber) } },
  );
  return response.data;
}

export async function getFileEvents(projectId, filename, chamber) {
  const response = await apiService.get(
    `${PROJECTS_BASE}/${projectId}/files/${encodeURIComponent(filename)}/events`,
    { params: { chamber: String(chamber) } },
  );
  return response.data;
}

export async function getFilePlotConfig(projectId, filename, chamber) {
  const response = await apiService.get(
    `${PROJECTS_BASE}/${projectId}/files/${encodeURIComponent(filename)}/plot_config`,
    { params: { chamber: String(chamber) } },
  );
  return response.data;
}

// ============================================================================
// Project Sync
// ============================================================================

export async function getSyncStatus(projectId) {
  const response = await apiService.get(`${PROJECTS_BASE}/${projectId}/sync`);
  return response.data;
}

export async function triggerSync(projectId) {
  const response = await apiService.post(`${PROJECTS_BASE}/${projectId}/sync`);
  return response.data;
}
